import { useCallback, useEffect, useState } from 'react'
import { Copy, Pencil, Trash2, Plus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchCanned,
  createCanned,
  updateCanned,
  useCanned,
  deleteCanned,
  CATEGORIES,
  CN_STR as S,
  type CannedResponse,
} from './cannedApi'

export function CannedResponsesScreen() {
  const { toast } = useToast()
  const [category, setCategory] = useState('')
  const [rows, setRows] = useState<CannedResponse[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [editId, setEditId] = useState<number | null>(null) // null=none, 0=new
  const [fTitle, setFTitle] = useState('')
  const [fCategory, setFCategory] = useState('general')
  const [fBody, setFBody] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchCanned(category, ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, category])

  const openNew = () => {
    setEditId(0)
    setFTitle('')
    setFCategory('general')
    setFBody('')
  }
  const openEdit = (r: CannedResponse) => {
    setEditId(r.id)
    setFTitle(r.title)
    setFCategory(r.category)
    setFBody(r.body)
  }
  const save = async () => {
    if (!fTitle.trim() || !fBody.trim()) return
    setBusy(true)
    try {
      const payload = { title: fTitle.trim(), category: fCategory, body: fBody.trim() }
      if (editId && editId > 0) await updateCanned(editId, payload)
      else await createCanned(payload)
      setEditId(null)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const copy = async (r: CannedResponse) => {
    try {
      await navigator.clipboard.writeText(r.body)
      useCanned(r.id).then(reload).catch(() => {})
      toast(S.copied, { tone: 'success' })
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteCanned(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A7.15"
        description={S.desc}
        actions={
          <button type="button" onClick={openNew} className="flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white">
            <Plus size={14} />
            {S.create}
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Edit form */}
          {editId !== null && (
            <div className="space-y-2 rounded-lg border border-accent bg-surface-2 p-3">
              <div className="flex gap-2">
                <input
                  value={fTitle}
                  onChange={(e) => setFTitle(e.target.value)}
                  placeholder={S.name}
                  className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                />
                <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <textarea
                value={fBody}
                onChange={(e) => setFBody(e.target.value)}
                placeholder={S.body}
                rows={4}
                className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <div className="flex gap-2">
                <button type="button" onClick={save} disabled={busy || !fTitle.trim() || !fBody.trim()} className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">
                  {S.save}
                </button>
                <button type="button" onClick={() => setEditId(null)} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">
                  {S.cancel}
                </button>
              </div>
            </div>
          )}

          <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary">
            <option value="">{S.all}</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-primary">{r.title}</span>
                        <span className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-tertiary">{r.category}</span>
                        {r.usage_count > 0 && <span className="text-xs text-tertiary">{r.usage_count} {S.used}</span>}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-secondary">{r.body}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button type="button" onClick={() => copy(r)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-accent" title={S.copy}>
                        <Copy size={15} />
                      </button>
                      <button type="button" onClick={() => openEdit(r)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-primary" title={S.edit}>
                        <Pencil size={15} />
                      </button>
                      <button type="button" onClick={() => del(r.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
